import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
    Button,
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionGroup,
    StandardModalDivider,
    StandardModalFormGroup,
    StandardModalHelperText,
    StandardModalLabel,
    StandardModalSection,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection,
    Toast,
    portalColors
} from "src/components";
import {
    createUserTag,
    deleteUserTag,
    updateUserTag
} from "src/scenes/EmployeeList/api/userTagsApi";
import Checkbox from "./Checkbox";

const Body = styled.div`
    display: grid;
    gap: 1rem;
    min-height: 0;
`;

const Row = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;

    > input {
        flex: 1 1 18rem;
    }
`;

const FooterSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    flex-wrap: wrap;
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const LeftButtons = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
    width: ${p => p.width || "172px"};
    height: ${p => p.height || "39px"};
    border-radius: ${p => p.borderRadius || "7px"} !important;
    box-shadow: 0px 3px 6px #00000029;
    opacity: 1;
    display: flex;
    justify-content: center;
    align-items: center;

    background: ${p => p.background || portalColors.surface};
    color: ${p => p.color || portalColors.text};
    border: ${p => p.border || `1px solid ${portalColors.borderStrong}`};
`;

const CheckboxCell = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const AssignedUsers = styled.span`
    font-weight: 700;
`;

const TagPill = styled.span`
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 0.32rem 0.75rem;
    border-radius: 7px;
    background: var(--calendar-dark-ink, #171A22);
    color: var(--calendar-dark-control-text, #FFFFFF);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
`;

export const StyledInput = styled.input`
    color: ${portalColors.text};
    padding: 0 1rem;
    margin: 0;
    height: 2.5rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    text-align: left;
    width: 475px;
    max-width: 100%;
    box-sizing: border-box;
    background: ${portalColors.surfaceSoft};
    box-shadow: 0px 3px 6px #00000029;
    border: 1px solid ${portalColors.border};
    border-radius: 12px;
    opacity: 1;

    ::placeholder {
        text-align: left;
    }
`;

const ModalContainer = styled.div`
    position: relative;
    min-width: 0;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: color-mix(in srgb, var(--calendar-surface, #FFFFFF) 78%, transparent);
    z-index: 999;
`;

const SelectableTable = styled(StandardModalTable)`
    tr.selected td {
        background: var(--calendar-selected-cell-bg, #eaf3ff);
    }

    tr.selection-disabled {
        cursor: not-allowed;
    }

    tr.selection-disabled:not(.selected) td {
        opacity: 0.45;
    }
`;

const UserTagsManagerModal = (props) => {
    const {
        open,
        onClose,
        token,
        tags,
        onChanged
    } = props;
    const inputRef = useRef(null);
    const [name, setName] = useState("");
    const [selectedTagUuids, setSelectedTagUuids] = useState([]);
    const [editingTagUuid, setEditingTagUuid] = useState(null);
    const [saving, setSaving] = useState(false);

    const selectedTag = selectedTagUuids.length === 1
        ? tags.find(tag => tag.uuid === selectedTagUuids[0])
        : null;
    const hasSelectedTags = selectedTagUuids.length > 0;
    const canEditTag = selectedTagUuids.length === 1;
    const isEditingTag = Boolean(editingTagUuid);
    const canSaveTag = name.trim().length > 0 && selectedTagUuids.length <= 1;
    const emptyRows = Math.max(0, 10 - tags.length);

    const toggleSelectedTag = (tagUuid) => {
        setSelectedTagUuids(prev => {
            if (isEditingTag) {
                if (editingTagUuid !== tagUuid) {
                    return prev;
                }

                return prev.includes(tagUuid)
                    ? []
                    : [tagUuid];
            }

            const isSelected = prev.includes(tagUuid);

            return isSelected
                ? prev.filter(uuid => uuid !== tagUuid)
                : [...prev, tagUuid];
        });
    };

    const resetEditingState = () => {
        setEditingTagUuid(null);
        setSelectedTagUuids([]);
        setName("");
    };

    const startEditingTag = () => {
        if (!selectedTag) {
            return;
        }

        setEditingTagUuid(selectedTag.uuid);
        setSelectedTagUuids([selectedTag.uuid]);
        setName(selectedTag.name);
    };

    const handleSaveTag = async () => {
        if (selectedTagUuids.length > 1) {
            return Toast.error("Select only one tag to edit.");
        }
        if (!name.trim()) {
            return Toast.error("Enter tag name.");
        }

        setSaving(true);
        try {
            if (selectedTag) {
                await updateUserTag(token, selectedTag.uuid, name.trim());
            } else {
                await createUserTag(token, name.trim());
            }
            resetEditingState();
            await onChanged();
            Toast.success(selectedTag ? "Tag updated successfully" : "Tag created successfully");
        } catch (apiError) {
            Toast.error(apiError.getApiErrorDetails().message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!hasSelectedTags) {
            return Toast.error("Select a tag.");
        }

        setSaving(true);
        try {
            await Promise.all(selectedTagUuids.map(tagUuid => deleteUserTag(token, tagUuid)));
            resetEditingState();
            await onChanged();
            Toast.success(selectedTagUuids.length > 1 ? "Tags deleted successfully" : "Tag deleted successfully");
        } catch {
            Toast.error(selectedTagUuids.length > 1 ? "Unable to delete selected tags." : "Unable to delete tag.");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (editingTagUuid && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingTagUuid]);

    useEffect(() => {
        if (selectedTagUuids.length === 0) {
            setEditingTagUuid(null);
            setName("");
        }
    }, [selectedTagUuids]);

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            widthMode="compactTable"
            title="User Tags Manager"
            description="Manage your user tags effortlessly. Create new tags, edit existing ones, or remove tags to keep your user groups organised for easy selection across the Me Business platform."
            footer={(
                <FooterSection>
                    <ButtonRow>
                        <LeftButtons>
                            {hasSelectedTags && (
                                <>
                                    {canEditTag && !isEditingTag && (
                                        <ActionButton
                                            background="#fff"
                                            color="#000"
                                            border="1px solid #000"
                                            width="140px"
                                            onClick={startEditingTag}
                                        >
                                            Edit Tag
                                        </ActionButton>
                                    )}

                                    <ActionButton
                                        background="#FFF5F5"
                                        color="#FF3B3B"
                                        border="1px solid #FF3B3B"
                                        width="140px"
                                        onClick={handleDelete}
                                    >
                                        {selectedTagUuids.length > 1 ? "Delete Tags" : "Delete Tag"}
                                    </ActionButton>
                                </>
                            )}
                        </LeftButtons>
                    </ButtonRow>
                    <StandardModalActionGroup $pushRight>
                        <ActionButton
                            background="#fff"
                            color="#000"
                            border="1px solid #000"
                            width="140px"
                            height="38px"
                            onClick={onClose}
                        >
                            Cancel
                        </ActionButton>

                        <ActionButton
                            background="#171A22"
                            color="#fff"
                            border="none"
                            width="140px"
                            height="38px"
                            onClick={onClose}
                        >
                            Save
                        </ActionButton>
                    </StandardModalActionGroup>
                </FooterSection>
            )}
        >
            <ModalContainer>
                {saving && (
                    <LoadingOverlay>
                        <LottieLoadingIndicator />
                    </LoadingOverlay>
                )}
                <Body>
                    <StandardModalSection>
                        <StandardModalFormGroup>
                            <StandardModalLabel>Create Tag</StandardModalLabel>
                            <StandardModalHelperText>
                                Enter a name for this tag. Example: Marketing Team, VIP Clients.
                            </StandardModalHelperText>
                        </StandardModalFormGroup>
                        <Row>
                            <StyledInput
                                ref={inputRef}
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                placeholder="Enter tag name"
                                maxLength="80"
                            />

                            <ActionButton
                                background={canSaveTag ? "#000" : "#D9D9D9"}
                                color={canSaveTag ? "#FFF" : "#888"}
                                border="none"
                                width="140px"
                                disabled={!canSaveTag}
                                onClick={handleSaveTag}
                                style={{
                                    cursor: canSaveTag ? "pointer" : "not-allowed"
                                }}
                            >
                                Save Tag
                            </ActionButton>
                        </Row>
                    </StandardModalSection>
                    <StandardModalDivider />
                    <StandardModalTableSection>
                        <StandardModalTableScroll maxHeight="22rem">
                            <SelectableTable>
                                <thead>
                                    <tr>
                                        <th align="left">{tags.length} Tags Available</th>
                                        <th align="left">Users Assigned</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {tags.map(tag => {
                                        const isSelected = selectedTagUuids.includes(tag.uuid);
                                        const assignedCount = tag.assignedUsers ? tag.assignedUsers.length : 0;

                                        return (
                                            <tr
                                                key={tag.uuid}
                                                className={`${isSelected ? "selected" : ""} ${isEditingTag ? "selection-disabled" : ""}`}
                                                onClick={() => {
                                                    if (isEditingTag && editingTagUuid !== tag.uuid) {
                                                        return;
                                                    }
                                                    toggleSelectedTag(tag.uuid);
                                                }}
                                            >
                                                <td>
                                                    <CheckboxCell>
                                                        <Checkbox
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={isEditingTag}
                                                            readOnly
                                                        />
                                                        <TagPill>{tag.name}</TagPill>
                                                    </CheckboxCell>
                                                </td>

                                                <td>
                                                    <AssignedUsers>
                                                        {assignedCount} Users Assigned
                                                    </AssignedUsers>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {Array.from({ length: emptyRows }).map((_, index) => (
                                        <tr key={`empty-${index}`}>
                                            <td>
                                                <CheckboxCell>
                                                    <Checkbox type="checkbox" disabled />
                                                    <span style={{ opacity: 0 }}>placeholder</span>
                                                </CheckboxCell>
                                            </td>
                                            <td>
                                                <span style={{ opacity: 0 }}>0 Users Assigned</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </SelectableTable>
                        </StandardModalTableScroll>
                    </StandardModalTableSection>
                </Body>
            </ModalContainer>
        </StandardModal>
    );
};

export default UserTagsManagerModal;
